<template>
  <v-container fluid>
    <div class="toolbar d-flex flex-row flex-wrap justify-space-between align-center elevation-3 mb-3">
      <span>
        Performance Evaluations
      </span>
      <div
        class="d-flex flex-row align-center"
        style="gap: 0.625rem;">
        <v-select
          v-model="query.date"
          class="date-picker-input"
          :items="years"
          :menu-props="{ offsetY: true }"
          :rules="requiredRules"
          label="Year"
          prepend-icon="mdi-calendar"
          color="secondary"
          solo
          filled
          dense
          hide-details />
        <v-btn
          color="success"
          rounded
          small
          :loading="loading"
          @click="getItems()">
          Search
        </v-btn>
        <v-btn
          v-if="isDeveloper"
          color="secondary"
          rounded
          small
          :loading="isSnapping"
          :disabled="loading"
          @click="openSnapshotDialog()">
          Snapshot
        </v-btn>
      </div>
    </div>

    <v-row class="px-8">
      <v-col
        cols="12"
        class="card-container elevation-3">
        <v-data-table
          :headers="headers"
          :items="items"
          :items-per-page="-1"
          :loading="loading"
          hide-default-footer>
          <!-- <template #[`item.warehouse.name`]="{ item }">
            <v-btn
              color="secondary"
              small
              text
              :to="`/pms/evaluation/${item.warehouse.id}?date=${query.date}`">
              <span class="text-decoration-underline">
                {{ item.warehouse.name }}
              </span>
            </v-btn>
          </template> -->
          <template
            v-for="(month, index) in months"
            #[`item.${month.name}`]="{ item }">
            <v-btn
              v-if="item[month.name]"
              :key="`${item.warehouse.id}-${index}`"
              color="secondary"
              small
              text
              :to="`/pms/evaluation/${item.warehouse.id}?month=${month.id}&year=${query.date}`">
              <span class="text-decoration-underline">
                {{ item[month.name] | showNumberFormat }}
              </span>
            </v-btn>
            <span
              v-else
              :key="`${item.warehouse.id}-${index}`">
              -
            </span>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-dialog
      v-model="snapshotDialog"
      max-width="500px"
      persistent>
      <v-card>
        <v-card-title>
          <h3>
            Snapshot Evaluation
          </h3>
          <span class="grey--text text-caption">
            Please select target month and store to snapshot this evaluation.
          </span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-form
              ref="snapshot-form"
              @submit.prevent="onConfirmSnapshot()">
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="snapshopFormData.week"
                    :items="weeks"
                    :menu-props="{ offsetY: true }"
                    :rules="requiredRules"
                    label="Week"
                    color="secondary"
                    outlined
                    dense
                    hide-details />
                </v-col>
                <v-col cols="12">
                  <WarehouseSelect
                    v-model="snapshopFormData.warehouse"
                    :items="allWarehouses"
                    item-text="name"
                    item-value="id"
                    :menu-props="{ offsetY: true }"
                    label="Warehouse"
                    color="secondary"
                    outlined
                    dense
                    hide-details
                    return-object />
                </v-col>
                <v-col cols="12">
                  <DatePicker
                    v-model="snapshopFormData.date"
                    type="month"
                    :max="maxMonth"
                    color="secondary"
                    outlined />
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="secondary"
            text
            :disabled="isSnapping"
            @click="onClose()">
            Cancel
          </v-btn>
          <v-btn
            :loading="isSnapping"
            color="success"
            text
            @click="onConfirmSnapshot()">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import DatePicker from '@/components/DatePicker.vue'
import WarehouseSelect from '@/components/WarehouseSelect.vue'
import PerformanceEvaluationsProvider from '@/resources/PerformanceEvaluationsProvider'
import EvaluationTopics from '@/assets/json/EvaluationTopics.json'
import { getRole } from '@/assets/js/Authentication'

const PerformanceEvaluationsService = new PerformanceEvaluationsProvider()

export default {
  components: {
    DatePicker,
    WarehouseSelect
  },
  data () {
    return {
      rawItems: [],
      items: [],
      query: {
        date: this.$dayjs().format('YYYY')
      },
      minYear: this.$dayjs().subtract(5, 'year').format('YYYY'),
      maxYear: this.$dayjs().format('YYYY'),
      months: [
        {
          id: 1,
          name: 'Jan'
        },
        {
          id: 2,
          name: 'Feb'
        },
        {
          id: 3,
          name: 'Mar'
        },
        {
          id: 4,
          name: 'Apr'
        },
        {
          id: 5,
          name: 'May'
        },
        {
          id: 6,
          name: 'Jun'
        },
        {
          id: 7,
          name: 'Jul'
        },
        {
          id: 8,
          name: 'Aug'
        },
        {
          id: 9,
          name: 'Sep'
        },
        {
          id: 10,
          name: 'Oct'
        },
        {
          id: 11,
          name: 'Nov'
        },
        {
          id: 12,
          name: 'Dec'
        }
      ],
      mainTopics: EvaluationTopics,
      loading: false,
      requiredRules: [
        (v) => !!v || 'This field is required!'
      ],
      weeks: [1, 2, 3, 4],
      maxMonth: this.$dayjs().format('YYYY-MM'),
      snapshotDialog: false,
      isSnapping: false,
      snapshopFormData: {
        week: 1,
        date: this.$dayjs().format('YYYY-MM'),
        warehouse: null
      }
    }
  },
  computed: {
    isDeveloper () {
      return getRole() === 'developer'
    },
    allWarehouses () {
      return [
        {
          id: null,
          name: 'All Warehouses'
        },
        ...this.warehouses
      ]
    },
    warehouses () {
      return this.$store.getters['Store/warehouse']
        ?.filter((warehouse) => warehouse.id !== 0 && warehouse.type === 'store' && warehouse.status === 'active')
        ?.sort((a, b) => a.name.localeCompare(b.name)) || []
    },
    headers () {
      const isCurrentYear = this.query.date === this.$dayjs().format('YYYY')
      const reversalMonths = [...this.months]
        .filter((month) => (isCurrentYear ? month.id <= this.$dayjs().month() + 1 : true))
        .sort((a, b) => b.id - a.id)

      return [
        {
          text: 'Branch',
          value: 'warehouse.name',
          sortable: false,
          width: '240px',
          class: 'sticky-first sticky-border',
          cellClass: 'sticky-first sticky-border'
        },
        ...reversalMonths.map((month) => ({
          text: month.name,
          value: month.name,
          sortable: false,
          width: '110px',
          align: 'center'
        }))
      ]
    },
    years () {
      const years = []
      for (let i = Number(this.maxYear); i >= Number(this.minYear); i--) {
        years.push(`${i}`)
      }
      return years
    }
  },
  watch: {
    'query.date': {
      handler () {
        this.getItems()
      },
      deep: true
    }
  },
  mounted () {
    this.getItems()
  },
  methods: {
    async getItems () {
      try {
        this.loading = true

        const { data } = await PerformanceEvaluationsService.getItems({
          startDate: this.$dayjs(this.query.date)
                      .startOf('year')
                      .hour(0)
                      .minute(0)
                      .second(0)
                      .utc()
                      .toISOString(),
          endDate: this.$dayjs(this.query.date)
                      .endOf('year')
                      .hour(23)
                      .minute(59)
                      .second(59)
                      .utc()
                      .toISOString()
        })

        this.rawItems = data
        this.items = this.warehouses.map((warehouse) => {
          const results = data.filter((v) => v.warehouse.id === warehouse.id)
          const mappedMonths = this.months.reduce((obj, month) => {
            const tmpObj = { ...obj }
            const found = results.filter((v) => v.month === month.id)

            tmpObj[month.name] = found.length > 0
              ? found.reduce((acc, topic) => acc + (topic?.totalScore || 0), 0) / found.length
              : null

            return tmpObj
          }, {})

          return {
            warehouse,
            ...mappedMonths
          }
        })
      } catch (error) {
        console.error('getItems', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    openSnapshotDialog () {
      this.snapshotDialog = true
    },
    async onConfirmSnapshot () {
      const isValid = await this.$refs['snapshot-form'].validate()

      if (!isValid) {
        return
      }

      this.$store.dispatch('Components/setModal', {
        value: true,
        title: 'Snapshot Evaluation',
        message: 'Do you want to snapshot this evaluation?',
        confirmText: 'Snapshot',
        confirmType: 'success',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.onSnapshot()
      })
    },
    onClose () {
      this.snapshotDialog = false
      this.isSnapping = false
      this.snapshopFormData = {
        week: 1,
        date: this.$dayjs().format('YYYY-MM'),
        warehouse: null
      }
    },
    async onSnapshot () {
      try {
        this.isSnapping = true

        const year = Number(this.snapshopFormData.date.split('-')[0])
        const month = Number(this.snapshopFormData.date.split('-')[1])

        await PerformanceEvaluationsService.snapshotEvaluation({
          warehouse: this.snapshopFormData.warehouse?.id || null,
          week: this.snapshopFormData.week,
          month,
          year
        })

        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'Snapshot evaluation successfully!',
          type: 'success'
        })

        this.onClose()
        this.getItems()
      } catch (error) {
        console.error('onSnapshot', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.isSnapping = false
      }
    }
  }
}
</script>

<style scoped>
.toolbar {
  position: sticky;
  top: 70px;
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.toolbar span {
  font-weight: bold;
  font-size: 1.25rem;
}
.card-container {
  padding: 1.25rem 1.875rem;
  background-color: #fff;
  border-radius: 0.625rem;
  margin-top: 1.25rem;
}
.card-title {
  font-weight: bold;
  font-size: 1.35rem;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th.sticky-first,
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td.sticky-first,
::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th.sticky-second,
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td.sticky-second {
  position: sticky;
  background: white;
  z-index: 2;
}

::v-deep .sticky-first {
  left: 0;
}
::v-deep .sticky-second {
  left: 100px;
}
::v-deep .sticky-border {
  border-right: 1px solid #e0e0e0;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th.sticky-first,
::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th.sticky-second {
  z-index: 3;
}
</style>
